// Copyright (c) 2021 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { Button, Collapse } from "react-bootstrap";
import { RoutedProps, withRouter } from "view/reactrouterinterceptor";
import JsonView from "@uiw/react-json-view";

import { Command } from "graphql/api";
import { DeviceType, isStratoDevice } from "shared/types/globtypes";
import { CloudSettings, IVirtSiteDefinition } from "shared/types/cloudtypes";
import { DataState } from "types";
import { isSet } from "shared/utils/utils";
import { IAction, ActionType, raiseAction } from "actions";
import {
  IActionTypeChangeSite,
  IActionCommand,
  IActionTypeConfirmPopup,
  IActionSaveVirtualParameters,
} from "actions";
import { IState } from "states/states";
import { SiteSettingsArea } from "states/guistates";
import { Company, Site } from "states/sitestates";
import { IPendingCommand } from "states/schedulerstates";
import { isStateDirty, makeCommandSeqNr, toDropdownItems } from "guiutils";

import SiteSettings from "view/comp/SiteSettings";
import AlarmSettings from "view/comp/AlarmSettings";
import ModalTrendMonitorSettings from "view/modal/ModalTrendMonitorSettings";
import Importer from "view/comp/Importer";
import SaveFeedback from "view/comp/SaveFeedback";
import Dropdown from "view/comp/Dropdown";
import HelpIcon from "view/comp/HelpIcon";

import IconExpandMore from "icons/iconExpandMore";
import IconExpandLess from "icons/iconExpandLess";

interface ISiteAdminProps {
  site: Site | undefined;
  cloudSettings: CloudSettings | undefined;
  companyList: Company[];
  canModifySites: boolean;
  sshTunnelPort: number | undefined; // relay port of the user, undefined hides the tunnel button
  canRebootController: boolean;
  dataState: DataState;
  pendingCommand: IPendingCommand | undefined;
  canModifyTrendMonitors: boolean;
  canModifyAlarms: boolean;
  canImportTrendData: boolean;
  canSetVirtualParameters: boolean;
  virtualSite: IVirtSiteDefinition | undefined;
  openedSiteSettingsArea: SiteSettingsArea[] | undefined;
}

const mapStateToProps = (state: IState) => {
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);
  const siteId = state.cached.selectedSite;
  const site = state.site.siteList?.find((curSite) => curSite.id === siteId);
  if (site === undefined) {
    console.error("admin unidentified site", siteId, state.site.siteList);
  }

  const props: ISiteAdminProps = {
    site,
    cloudSettings: site?.cloudSettings,
    companyList: state.site.companyList ?? [],
    canModifySites: curUser?.accessRights.modifySites ?? false,
    sshTunnelPort:
      isSet(state.scheduler.allData.lastData) && isStratoDevice(site?.deviceType) ?
        curUser?.accessRights.openSshTunnel
      : undefined,
    canRebootController:
      Boolean(curUser?.accessRights.controllerAdmin) &&
      isSet(state.scheduler.allData.lastData) &&
      isStratoDevice(site?.deviceType),
    dataState: state.scheduler.saveState,
    pendingCommand: state.scheduler.pendingCommand,
    canModifyTrendMonitors: curUser?.accessRights.crudTrendMonitors ?? false,
    canModifyAlarms: curUser?.accessRights.modifyAlarms ?? false,
    canImportTrendData: curUser?.accessRights.importTrendData ?? false,
    canSetVirtualParameters:
      site?.deviceType === DeviceType.VIRTUAL &&
      (curUser?.accessRights.setVirtualParameters ?? false),
    virtualSite: state.para.parameterVirtual,
    openedSiteSettingsArea: state.gui.openedSiteSettingsArea,
  };
  return props;
};

type ISiteAdminState = {
  cloudSettings?: CloudSettings;
  companyId?: number;
  isVirtualParametersExpanded?: boolean;
  isVirtualSiteEditing?: boolean;
  isVirtualSiteDefinitionValid?: boolean;
  virtualSiteDefinition?: string;
};

interface ISiteAdminDispatch {
  onSshTunnelClick: (title: string, message: string) => void;
  onRebootClick: (title: string, message: string) => void;
  onEditTrendMonitor: (index?: number) => void;
  onOkClick: (id: number, companyId: number | undefined, cloudSettings: CloudSettings) => void;
  onChanges: (dataState: DataState) => void;
  onSaveVirtualParameters: (siteId: number, virtualSiteDefinition: IVirtSiteDefinition) => void;
  onSiteSettingsAreaToggle: (areas: SiteSettingsArea[] | undefined, area: SiteSettingsArea) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ISiteAdminDispatch = {
    onSshTunnelClick: (title: string, message: string) => {
      const confirmPayload: IActionCommand = {
        commandSeqNr: makeCommandSeqNr(),
      };
      const payload: IActionTypeConfirmPopup = {
        title,
        message,
        action: ActionType.ACT_OPEN_SSH_TUNNEL_REQ,
        actionPayload: confirmPayload,
      };
      dispatch(raiseAction(ActionType.ACT_CONFIRM_POPUP, payload));
    },
    onRebootClick: (title: string, message: string) => {
      const confirmPayload: IActionCommand = {
        commandSeqNr: makeCommandSeqNr(),
      };
      const payload: IActionTypeConfirmPopup = {
        title,
        message,
        action: ActionType.ACT_REBOOT_REQ,
        actionPayload: confirmPayload,
      };
      dispatch(raiseAction(ActionType.ACT_CONFIRM_POPUP, payload));
    },
    onEditTrendMonitor: (index?: number) => {
      dispatch(raiseAction(ActionType.ACT_EDIT_TRENDMONITOR, index));
    },
    onOkClick: (id: number, companyId: number | undefined, cloudSettings: CloudSettings) => {
      const payload: IActionTypeChangeSite = {
        id,
        companyId,
        cloudSettings,
      };
      dispatch(raiseAction(ActionType.ACT_CHANGE_SITESETTINGS_REQ, payload));
    },
    onChanges: (dataState: DataState) => {
      if (!isStateDirty(dataState)) {
        dispatch(raiseAction(ActionType.ACT_SET_PAGE_DIRTY, true));
      }
    },
    onSaveVirtualParameters: (siteId: number, virtualSite: IVirtSiteDefinition) => {
      const payload: IActionSaveVirtualParameters = {
        siteId,
        virtualSite,
      };
      dispatch(raiseAction(ActionType.ACT_SAVE_VIRTUAL_PARAMETERS_REQ, payload));
    },
    onSiteSettingsAreaToggle: (areas: SiteSettingsArea[] | undefined, area: SiteSettingsArea) => {
      if (!areas) {
        areas = [area];
      } else {
        areas = areas.includes(area) ? [...areas.filter((a) => a !== area)] : [...areas, area];
      }
      dispatch({
        type: "ACT_SITE_SETTINGS_AREA_TOGGLE",
        payload: areas,
      });
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  selectCompany: {
    id: "Select company",
  },
  confirmSshTunnelTitle: {
    id: "Open SSH Tunnel",
  },
  confirmRebootTitle: {
    id: "Reboot Controller",
  },
  confirmRebootBody: {
    id: "This will reboot the enline controller, which will stop energy regulation and may last several minutes. Are you sure?",
  },
  selectDeletionCaption: {
    id: "Select deletion strategy",
  },
});

// messages with placeholder values
const valueMessages = defineMessages<{
  confirmSshTunnelBody: { port: number };
}>({
  confirmSshTunnelBody: {
    id: "This will open an SSH tunnel to the embyt server. Connect via ssh -p {port} embyt@enl-tun.embyt.com. Are you sure?",
  },
});

class SiteAdminComponent extends React.Component<
  ISiteAdminProps & ISiteAdminDispatch & RoutedProps,
  ISiteAdminState
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  state: ISiteAdminState = {};
  private readonly virtSiteDefinitionHelp = `
interface IVirtSiteDefinition {
  input?: IVirtDefinition[];
  energymeter?: IVirtDefinition[];
  tariffline?: IVirtDefinition[];
  regulation?: IVirtDefinition[];
  output?: IVirtDefinition[];
  charger?: IVirtDefinition[];
  ocpp?: IVirtDefinition[];
}

interface IVirtDefinition {
  sourceSite: number;
  // either sourceIndex, sourceId or sourceName must be set
  sourceIndex?: number; // array index of the source
  sourceId?: number; // id of the source
  sourceName?: string; // regular expression to match source name; not allowed for ocpp chargers
  // possibilities to overwrite source parameter, e.g. its name
  name?: string;
  isEnabled?: boolean;
  webActive?: boolean;
  webCtrl?: boolean;
}`;

  componentDidMount() {
    this.setState({
      cloudSettings: this.props.cloudSettings,
      companyId: this.props.site?.companyId,
    });
  }

  public render() {
    return (
      <div id="siteadmin">
        <div className="savebox">
          {this.renderSaveSettings()}
          <SaveFeedback className="mb-2 mt-2" dataState={this.props.dataState} />
        </div>
        <h1>
          <FormattedMessage id="Site Administration" />
        </h1>
        {this.renderButtons()}
        {this.props.canModifySites &&
          this.props.companyList.length > 1 &&
          this.props.companyList.some((c) => c.id !== this.props.site?.companyId) &&
          this.renderCompanySelection()}
        {this.props.canModifySites && (
          <SiteSettings
            cloudSettings={this.state.cloudSettings}
            onSetCloudSettings={(cloudSettings) => {
              this.setState({ cloudSettings });
              this.props.onChanges(this.props.dataState);
            }}
          />
        )}
        {this.props.canModifyAlarms && (
          <AlarmSettings
            settings={this.state.cloudSettings?.alarm}
            onChange={(newSettings) => {
              this.setState({
                cloudSettings: {
                  ...this.state.cloudSettings,
                  alarm: newSettings,
                },
              });
              this.props.onChanges(this.props.dataState);
            }}
          />
        )}
        {this.props.canModifyTrendMonitors &&
          this.props.site?.deviceType !== DeviceType.VIRTUAL &&
          this.renderTrendMonitors()}
        {this.props.canImportTrendData && this.renderImports()}
        {this.props.canSetVirtualParameters && this.renderVirtualParameters()}
      </div>
    );
  }

  private renderSaveSettings() {
    return (
      <div className="mb-2 mt-2">
        {isSet(this.props.site) && isStateDirty(this.props.dataState) && (
          <Button
            variant="primary"
            id="saveSiteAdmin"
            type="submit"
            className="ms-2"
            onClick={(_ev) => {
              // use report settings from props
              const cloudSettings = {
                ...this.state.cloudSettings,
              };
              // only send companyId if it was changed to inhibit access rights problems
              const companyId =
                this.state.companyId !== this.props.site?.companyId ?
                  this.state.companyId
                : undefined;
              this.props.onOkClick(this.props.site!.id, companyId, cloudSettings);
            }}
          >
            <FormattedMessage id="Save Changes" />
          </Button>
        )}
      </div>
    );
  }

  private renderButtons() {
    return (
      <div className="buttonbox mb-3">
        {this.props.canRebootController && (
          <Button
            id="reboot"
            variant="danger"
            onClick={(e) =>
              this.props.onRebootClick(
                this.context.formatMessage(messages.confirmRebootTitle),
                this.context.formatMessage(messages.confirmRebootBody),
              )
            }
          >
            <FormattedMessage id="Reboot Controller" />
          </Button>
        )}
        {this.props.pendingCommand?.command === Command.rebootController && (
          <div className="alert alert-warning">
            <FormattedMessage id="Execution Pending" />
          </div>
        )}
        {this.props.sshTunnelPort !== undefined && (
          <Button
            id="sshTunnel"
            variant="outline-success"
            onClick={(e) =>
              this.props.onSshTunnelClick(
                this.context.formatMessage(messages.confirmSshTunnelTitle),
                this.context.formatMessage(valueMessages.confirmSshTunnelBody, {
                  port: this.props.sshTunnelPort!,
                }),
              )
            }
          >
            <FormattedMessage id="Open SSH Tunnel" />
          </Button>
        )}
        {this.props.pendingCommand?.command === Command.openSshTunnel && (
          <div className="alert alert-warning">
            <FormattedMessage id="Execution Pending" />
          </div>
        )}
      </div>
    );
  }

  private renderCompanySelection() {
    return (
      <div className="mb-3">
        <table>
          <tbody>
            <tr>
              <td>
                <FormattedMessage id="Company" />:
              </td>
              <td>
                <Dropdown
                  className="btn-group-sm"
                  items={toDropdownItems(
                    this.props.companyList.map((c) => c.id),
                    "company",
                    (_name, companyId) => {
                      this.setState({ companyId: companyId });
                      this.props.onChanges(this.props.dataState);
                    },
                    (companyId) =>
                      this.props.companyList.find((c) => c.id === companyId)?.name ??
                      String(companyId),
                  )}
                  caption={this.context.formatMessage(messages.selectCompany)}
                  curItem={
                    this.props.companyList.find((c) => c.id === this.state.companyId)?.name ?? ""
                  }
                  disabled={this.props.dataState === DataState.SAVE_REQ}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  private renderTrendMonitors() {
    const trendMonitors = this.props.cloudSettings?.trendMonitor ?? [];
    const trendMonitorItems = Array.from(Array(trendMonitors.length).keys())
      .sort((a, b) => trendMonitors[a].title.localeCompare(trendMonitors[b].title))
      .map((curIndex) => {
        const curItem = trendMonitors[curIndex];
        const linkClasses = ["link"];
        if (!curItem.isActive) linkClasses.push("deactivated");
        return (
          <tr key={curIndex} className="mt-1 mb-1">
            <td>
              <span
                onClick={() => this.props.onEditTrendMonitor(curIndex)}
                className={linkClasses.join(" ")}
              >
                {curItem.title}
              </span>
            </td>
          </tr>
        );
      });

    return (
      <div id="trendmonitorlist">
        <h1>
          <span
            className="link"
            onClick={() => {
              this.props.onSiteSettingsAreaToggle(
                this.props.openedSiteSettingsArea,
                SiteSettingsArea.TRENDMONITOR,
              );
            }}
          >
            <Button variant="link" className="expand-button me-2 d-print-none">
              {this.props.openedSiteSettingsArea?.includes(SiteSettingsArea.TRENDMONITOR) ?
                <IconExpandMore />
              : <IconExpandLess />}
            </Button>
            <FormattedMessage id="Trend Monitors" />
          </span>
        </h1>
        <Collapse in={this.props.openedSiteSettingsArea?.includes(SiteSettingsArea.TRENDMONITOR)}>
          <div id="trendmonitorentries">
            <table className="fullwidthwhite">
              <tbody>
                {trendMonitorItems}
                <tr>
                  <td>
                    <Button
                      id="newreport"
                      variant="outline-success"
                      className="mt-1 mb-1"
                      onClick={(e) => this.props.onEditTrendMonitor()}
                    >
                      <FormattedMessage id="Create New" />
                    </Button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Collapse>
        <ModalTrendMonitorSettings />
      </div>
    );
  }

  private renderImports() {
    return (
      <div id="trendimporter">
        <h1>
          <span
            className="link"
            onClick={() => {
              this.props.onSiteSettingsAreaToggle(
                this.props.openedSiteSettingsArea,
                SiteSettingsArea.TRENDIMPORTER,
              );
            }}
          >
            <Button variant="link" className="expand-button me-2 d-print-none">
              {this.props.openedSiteSettingsArea?.includes(SiteSettingsArea.TRENDIMPORTER) ?
                <IconExpandMore />
              : <IconExpandLess />}
            </Button>
            <FormattedMessage id="Import Trend Data" />
          </span>
        </h1>
        <Collapse in={this.props.openedSiteSettingsArea?.includes(SiteSettingsArea.TRENDIMPORTER)}>
          <div className="fullwidthwhite">
            <Importer />
          </div>
        </Collapse>
      </div>
    );
  }

  private renderVirtualParameters() {
    const hasData = this.props.virtualSite && Object.keys(this.props.virtualSite).length > 0;
    const isEditing = Boolean(this.state.isVirtualSiteEditing);

    return (
      <div id="virtualparameters">
        <h1>
          <span
            className="link"
            onClick={() => {
              this.props.onSiteSettingsAreaToggle(
                this.props.openedSiteSettingsArea,
                SiteSettingsArea.VIRTUALPARAMETERS,
              );
            }}
          >
            <Button variant="link" className="expand-button me-2 d-print-none">
              {this.props.openedSiteSettingsArea?.includes(SiteSettingsArea.VIRTUALPARAMETERS) ?
                <IconExpandMore />
              : <IconExpandLess />}
            </Button>
            <FormattedMessage id="Virtual Parameters" />
          </span>
        </h1>
        <Collapse
          in={this.props.openedSiteSettingsArea?.includes(SiteSettingsArea.VIRTUALPARAMETERS)}
        >
          <div className="fullwidthwhite p-3">
            <div className="mb-3">
              {isEditing ?
                <div>
                  <Button
                    variant="primary"
                    size="sm"
                    className="me-2"
                    disabled={
                      !this.state.isVirtualSiteDefinitionValid || !isSet(this.props.site?.id)
                    }
                    onClick={() => {
                      try {
                        this.props.onSaveVirtualParameters(
                          this.props.site!.id,
                          JSON.parse(this.state.virtualSiteDefinition ?? "{}"),
                        );
                        this.setState({ isVirtualSiteEditing: false });
                      } catch (error) {
                        alert("Invalid JSON format. Please check your syntax.");
                      }
                    }}
                  >
                    <FormattedMessage id="Save" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      this.setState({
                        isVirtualSiteEditing: false,
                      });
                    }}
                  >
                    <FormattedMessage id="Cancel" />
                  </Button>
                  <HelpIcon
                    className="ms-2"
                    text={
                      <>
                        The format of the JSON configuration object is as follow:
                        <pre>{this.virtSiteDefinitionHelp}</pre>
                      </>
                    }
                  />
                </div>
              : <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    this.setState({
                      isVirtualSiteEditing: true,
                      isVirtualSiteDefinitionValid: true,
                      virtualSiteDefinition: JSON.stringify(this.props.virtualSite, null, 2),
                    });
                  }}
                >
                  <FormattedMessage id="Edit" />
                </Button>
              }
            </div>
            {isEditing ?
              <textarea
                className={`form-control ${
                  this.state.isVirtualSiteDefinitionValid ? "" : "input-error"
                }`}
                rows={20}
                value={this.state.virtualSiteDefinition}
                onChange={(e) => {
                  this.setState({ virtualSiteDefinition: e.target.value });
                  try {
                    JSON.parse(e.target.value);
                    this.setState({ isVirtualSiteDefinitionValid: true });
                  } catch (error) {
                    this.setState({ isVirtualSiteDefinitionValid: false });
                  }
                }}
                style={{
                  fontFamily: "monospace",
                  fontSize: "12px",
                }}
              />
            : hasData ?
              <JsonView
                value={this.props.virtualSite}
                displayDataTypes={false}
                displayObjectSize={false}
                collapsed={1}
                style={{
                  backgroundColor: "#f8f9fa",
                  borderRadius: "4px",
                  padding: "10px",
                }}
              />
            : <div className="text-muted">
                <FormattedMessage id="No virtual parameters available" />
              </div>
            }
          </div>
        </Collapse>
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(SiteAdminComponent));
